/**
 * MonthView — react-day-picker month grid with per-day event dots.
 *
 * Days that carry ≥1 event render up to 3 coloured dots below the
 * date number (colours drive from `TYPE_META.dotColor`). Clicking a
 * populated day sets `selectedDate`, which the parent uses to open
 * the day drawer.
 */
import { useMemo } from "react";
import { DayPicker } from "react-day-picker";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { groupByDate, dateKey, TYPE_META } from "./utils";

export default function MonthView({ items, month, onMonthChange, selectedDate, onDayClick }) {
  const buckets = useMemo(() => groupByDate(items), [items]);

  return (
    <div
      data-testid="calendar-month-view"
      className="rounded-3xl p-4"
      style={{
        background: "var(--v2-surface)",
        border: "1px solid var(--v2-border)",
      }}
    >
      <DayPicker
        mode="single"
        selected={selectedDate}
        month={month}
        onMonthChange={onMonthChange}
        onDayClick={(d) => {
          const key = dateKey(d);
          if (buckets[key]?.length) onDayClick(d);
        }}
        showOutsideDays={false}
        className="w-full"
        classNames={{
          months: "flex flex-col w-full",
          month: "space-y-4 w-full",
          caption: "flex justify-between pt-1 relative items-center px-2",
          caption_label:
            "text-sm font-semibold text-v2-text tracking-tight",
          nav: "space-x-1 flex items-center",
          nav_button: cn(
            buttonVariants({ variant: "outline" }),
            "h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100",
          ),
          nav_button_previous: "absolute left-2",
          nav_button_next: "absolute right-2",
          table: "w-full border-collapse mt-2",
          head_row: "flex w-full",
          head_cell:
            "flex-1 text-v2-textMuted rounded-md font-normal text-[10px] uppercase tracking-[0.16em] py-2",
          row: "flex w-full mt-1",
          cell: "flex-1 relative p-0.5 text-center text-sm focus-within:relative focus-within:z-20",
          day: cn(
            "relative h-14 w-full rounded-lg font-normal transition-colors flex flex-col items-center justify-start pt-1.5 text-v2-text hover:bg-white/[0.03]",
          ),
          day_today:
            "border border-v2-accent/40 text-v2-accent font-semibold bg-v2-accent/[0.06]",
          day_selected:
            "bg-v2-accent/[0.12] border border-v2-accent/60 text-v2-text",
          day_outside: "text-v2-textMuted/40",
          day_disabled: "text-v2-textMuted/30 opacity-40",
          day_hidden: "invisible",
        }}
        components={{
          IconLeft: ({ className, ...props }) => (
            <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
          ),
          IconRight: ({ className, ...props }) => (
            <ChevronRight className={cn("h-4 w-4", className)} {...props} />
          ),
          DayContent: ({ date }) => {
            const key = dateKey(date);
            const dayItems = buckets[key] || [];
            // Grab up to 3 unique dot colours per day so we don't
            // stack redundant dots for two same-type events.
            const dotColors = [];
            const seen = new Set();
            for (const it of dayItems) {
              const c = TYPE_META[it.event_type]?.dotColor;
              if (c && !seen.has(c)) {
                dotColors.push({ color: c, type: it.event_type, is_synth: it.is_synthesized });
                seen.add(c);
              }
              if (dotColors.length >= 3) break;
            }
            return (
              <div
                data-testid={`calendar-day-${key}`}
                data-event-count={dayItems.length}
                className="flex flex-col items-center justify-start h-full w-full"
              >
                <span>{date.getDate()}</span>
                {dotColors.length > 0 && (
                  <div
                    className="mt-1 flex items-center justify-center gap-0.5"
                    data-testid={`calendar-day-dots-${key}`}
                  >
                    {dotColors.map((d, i) => (
                      <span
                        key={i}
                        className="inline-block h-1.5 w-1.5 rounded-full"
                        style={{
                          background: d.color,
                          // synthesized: subtle ring so the dot
                          // reads as "computed" not "stored"
                          boxShadow: d.is_synth
                            ? `inset 0 0 0 1px ${d.color}, 0 0 0 1px rgba(0,0,0,0.35)`
                            : undefined,
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          },
        }}
      />
    </div>
  );
}
